<template text-black>
  <LayoutSection
    sectionClass="sticky z-[15] backdrop-blur-[22px] left-0 top-[7.8rem]"
    paddingClass="pt-4 pb-3"
  >
    <div v-if="!showFilters" class="results-toolbar">
      <div class="results-toolbar__info">
        <p class="results-toolbar__count">
          <template v-if="loading">{{ $t("Searching...") }}</template>
          <template v-else>
            <span class="results-toolbar__count-num">{{ resultCount }}</span>
            {{
              resultCount === 1
                ? $t("available professional")
                : $t("available professionals")
            }}
          </template>
        </p>
        <ul v-if="activeChips.length" class="results-toolbar__chips">
          <li v-for="chip in activeChips" :key="chip.key" class="results-chip">
            {{ chip.label }}
          </li>
        </ul>
      </div>
      <AppButton
        variant="secondary"
        class="results-toolbar__filters text-sm px-3 py-2"
        @click="openFilters"
      >
        <span>{{ $t("Filters") }}</span>
        <img src="@/assets/images/Filters.svg" alt="" class="w-6 h-6" />
      </AppButton>
    </div>

    <div v-else class="space-y-3">
      <div class="flex justify-end">
        <AppButton
          variant="secondary"
          class="results-toolbar__filters text-sm px-3 py-2"
          @click="closeFilters"
        >
          <span>{{ $t("Close") }}</span>
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </AppButton>
      </div>

      <SearchCommonSection v-model="filters" @search="handleSearch" />
    </div>
  </LayoutSection>
</template>

<script>
import SearchCommonSection from "@/components/search/searchCommonSection.vue";
import LayoutSection from "@/components/design/layoutSection.vue";
import AppButton from "@/components/buttons/AppButton.vue";
import dayjs from "dayjs";

export default {
  name: "SearchResultSection",
  components: {
    SearchCommonSection,
    LayoutSection,
    AppButton,
  },
  props: {
    resultCount: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filters: this.getFiltersFromUrl(),
      showFilters: false,
    };
  },
  computed: {
    activeChips() {
      const chips = [];
      const f = this.filters || {};
      if (f.online) {
        chips.push({ key: "online", label: this.$t("Online") });
      }
      if (f.inPerson) {
        chips.push({ key: "inPerson", label: this.$t("In-person") });
      }
      if (f.therapy) {
        chips.push({ key: "therapy", label: this.prettyTherapy(f.therapy) });
      }
      const address = f.location && f.location.address;
      if (address) {
        chips.push({ key: "location", label: address });
      }
      return chips;
    },
  },
  created() {
    const filters = this.getFiltersFromUrl();
    this.filters = filters;
  },
  watch: {
    "$route.query": {
      handler() {
        this.filters = this.getFiltersFromUrl();
      },
      deep: true,
    },
  },
  methods: {
    prettyTherapy(value) {
      return String(value)
        .replace(/[-_]+/g, " ")
        .trim()
        .replace(/^\w/, (c) => c.toUpperCase());
    },
    openFilters() {
      this.showFilters = true;
    },
    closeFilters() {
      this.showFilters = false;
    },
    handleSearch() {
      this.$emit("search");
    },
    getFiltersFromUrl() {
      const query = this.$route?.query || {};
      const onlineParam =
        typeof query.online === "string" ? query.online : undefined;
      const inPersonParam =
        typeof query.inPerson === "string" ? query.inPerson : undefined;

      const inPerson = inPersonParam === "1";
      const online = onlineParam === "1";

      const parseQueryDate = (rawValue) => {
        if (!rawValue) {
          return new Date();
        }
        if (rawValue instanceof Date) {
          const copy = new Date(rawValue.getTime());
          return Number.isNaN(copy.getTime()) ? new Date() : copy;
        }
        const value = String(rawValue);
        const format = process.env.URL_PARAM_DATE_FORMAT;
        if (format) {
          const parsed = dayjs(value, format, true);
          if (parsed.isValid()) {
            return parsed.toDate();
          }
        }
        const fallback = new Date(value);
        if (!Number.isNaN(fallback.getTime())) {
          return fallback;
        }
        return new Date();
      };

      return {
        location: {
          lat: parseFloat(query.lat) || null,
          lng: parseFloat(query.lng) || null,
          address: query.address || null,
        },
        online,
        inPerson,
        dateFrom: parseQueryDate(query.dateFrom),
        therapy: query.therapy || "",
        therapyType: query.therapyType || "",
        therapyObj: {
          uId: query.therapyId || null,
          type: query.therapyType || null,
        },
      };
    },
  },
  emits: ["search"],
};
</script>

<style scoped>
.results-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  flex-wrap: wrap;
}

.results-toolbar__info {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  flex-wrap: wrap;
  min-width: 0;
}

.results-toolbar__count {
  font-size: 1.5rem;
  font-weight: 500;
  color: #6f6a64;
  white-space: nowrap;
}

.results-toolbar__count-num {
  font-weight: 700;
  color: #1c1b1a;
  font-variant-numeric: tabular-nums;
}

.results-toolbar__chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.results-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(91, 111, 238, 0.22);
  background: rgba(91, 111, 238, 0.06);
  color: #3346d3;
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .results-toolbar__count {
    font-size: 1.4rem;
  }

  .results-toolbar :deep(.app-button) {
    min-height: 3.4rem;
    padding: 0.5rem 1rem;
    font-size: 1.2rem;
  }

  .results-toolbar :deep(.app-button img) {
    width: 1.6rem;
    height: 1.6rem;
  }
}
</style>
